import { Locale, PurchaseOrder, PurchaseOrderLine } from "@shared/types";
import { PDFFont, PDFPage, rgb } from "pdf-lib";
import { calculateAddressLines } from "./addressUtils";
import { CUSTOMER_LOGO_VARIANTS, CustomerLogoVariant, getCustomerAccountDisplayName, getCustomerLogoVariant } from "./customerUtils";
import { calculateColumnDimensions, Dimensions, getScaledFontSizeForSmallPaper, getScaledSpacing } from "./dimensionUtils";
import { formatProductTitle } from "./formattingUtils";
import { formatWeightWithLocale, getFixedT } from "./localeUtils";
import { createBeanzLogoImage, createKeepCupLogoImage } from "./logoUtils";
import { drawWrappedText, drawWrappedTextWithAlignment, normalizeTextForWinAnsi, TextAlignment, wrapText } from "./textUtils";

export const CUSTOMER_HEADER_LINE_HEIGHT = 20;
export const LOGO_TOP_OFFSET = 10;
export const LOGO_RETURN_PADDING = 3;

export const LOGO_DRAW_DIMENSIONS = {
  [CUSTOMER_LOGO_VARIANTS.BEANZ]: {
    width: { large: 60, small: 40 },
    height: { large: 18, small: 12 },
  },
  [CUSTOMER_LOGO_VARIANTS.KEEP_CUP]: {
    width: { large: 38, small: 28 },
    height: { large: 48, small: 36 },
  },
} as const satisfies Record<CustomerLogoVariant, { width: { large: number; small: number }; height: { large: number; small: number } }>;

export const drawHeader = (page: PDFPage, dimensions: Dimensions, boldFont: PDFFont, vendorLocale: Locale, recipientLocale?: Locale) => {
  const tVendor = getFixedT(vendorLocale);
  const tRecipient = recipientLocale && recipientLocale !== vendorLocale ? getFixedT(recipientLocale) : undefined;

  // Use recipient language as primary, vendor as fallback
  const headerText = tRecipient
    ? tRecipient("components.packingSlip.header", { defaultValue: "PACKING SLIP" })
    : tVendor("components.packingSlip.header", { defaultValue: "PACKING SLIP" });

  const fontSize = getScaledFontSizeForSmallPaper(9, 7, dimensions); // Smaller font for 3x4
  const normalizedHeaderText = normalizeTextForWinAnsi(headerText);
  const headerWidth = fontSize * normalizedHeaderText.length * 0.6; // Rough estimate
  const headerX = (dimensions.width - headerWidth) / 2;
  // Position header just below the top margin
  const headerY = dimensions.height - (dimensions.margin + 2);
  page.drawText(normalizedHeaderText, {
    x: headerX,
    y: headerY,
    font: boldFont,
    size: fontSize,
    color: rgb(0, 0, 0),
  });
};

interface CustomerLogoConfig {
  loadLogo: () => Promise<Uint8Array>;
  width: number;
  height: number;
}

const LOGO_LOADERS: Record<CustomerLogoVariant, () => Promise<Uint8Array>> = {
  [CUSTOMER_LOGO_VARIANTS.BEANZ]: createBeanzLogoImage,
  [CUSTOMER_LOGO_VARIANTS.KEEP_CUP]: createKeepCupLogoImage,
};

const getCustomerLogoConfig = (variant: CustomerLogoVariant, dimensions: Dimensions): CustomerLogoConfig => {
  const { width, height } = LOGO_DRAW_DIMENSIONS[variant];
  return {
    loadLogo: LOGO_LOADERS[variant],
    width: getScaledSpacing(width.large, width.small, dimensions),
    height: getScaledSpacing(height.large, height.small, dimensions),
  };
};

export const drawCustomerAccountName = async (
  page: PDFPage,
  purchaseOrder: PurchaseOrder,
  dimensions: Dimensions,
  startY: number,
  font: PDFFont
): Promise<number> => {
  const fontSize = getScaledFontSizeForSmallPaper(14, 10, dimensions); // Smaller font for 3x4
  const customerAccountDisplayName = getCustomerAccountDisplayName(purchaseOrder);
  const logoVariant = getCustomerLogoVariant(purchaseOrder);

  if (logoVariant) {
    try {
      const { loadLogo, width: logoWidth, height: logoHeight } = getCustomerLogoConfig(logoVariant, dimensions);
      const logoData = await loadLogo();
      const embeddedLogo = await page.doc.embedPng(logoData);

      page.drawImage(embeddedLogo, {
        x: dimensions.margin,
        y: dimensions.height - startY - logoHeight + LOGO_TOP_OFFSET,
        width: logoWidth,
        height: logoHeight,
      });

      return startY + logoHeight + LOGO_RETURN_PADDING;
    } catch (error) {
      console.warn(`Error drawing ${logoVariant} logo, falling back to text:`, error);
      const normalizedName = normalizeTextForWinAnsi(customerAccountDisplayName);
      page.drawText(normalizedName, {
        x: dimensions.margin,
        y: dimensions.height - startY,
        font,
        size: fontSize,
        color: rgb(0, 0, 0),
      });
    }
  } else {
    const normalizedName = normalizeTextForWinAnsi(customerAccountDisplayName);
    page.drawText(normalizedName, {
      x: dimensions.margin,
      y: dimensions.height - startY,
      font,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  }

  return startY + CUSTOMER_HEADER_LINE_HEIGHT;
};

export const drawAddress = (page: PDFPage, purchaseOrder: PurchaseOrder, dimensions: Dimensions, startY: number, font: PDFFont): number => {
  const { shippingAddress } = purchaseOrder;
  if (!shippingAddress) {
    return startY;
  }

  const addressLineHeight = 10;
  const formattedAddress = calculateAddressLines(shippingAddress);

  // Calculate address width - should be 50% of available width (leaving 50% for order info)
  const availableWidth = dimensions.width - dimensions.margin * 2;
  const addressWidth = availableWidth * 0.5; // 50% for address, 50% for order info

  let currentY = startY;
  for (const field of formattedAddress) {
    if (!field) {
      continue;
    }
    const fontSize = getScaledFontSizeForSmallPaper(9, 7, dimensions); // Smaller font for 3x4
    const heightUsed = drawWrappedText(page, field, {
      x: dimensions.margin,
      y: dimensions.height - currentY,
      maxWidth: addressWidth,
      fontSize,
      font,
      lineHeight: addressLineHeight,
    });
    currentY += heightUsed;
  }

  return currentY + addressLineHeight / 2; // Reduced spacing by half
};

export interface DrawOrderInfoOptions {
  page: PDFPage;
  purchaseOrder: PurchaseOrder;
  dimensions: Dimensions;
  startY: number;
  font: PDFFont;
  boldFont: PDFFont;
  vendorLocale: Locale;
  recipientLocale?: Locale;
  isEdiPartner?: boolean; // From purchaseOrder.isEdiPartner
  sltCustomerAccountNumber?: string; // From server-side env var
}

// Helper function to draw order info label (extracted to reduce cognitive complexity)
const drawOrderInfoLabel = (
  page: PDFPage,
  info: { vendorLabel: string; recipientLabel: string; needsTwoLines: boolean },
  dimensions: Dimensions,
  font: PDFFont,
  boldFont: PDFFont,
  numberProperties: Record<string, number>
) => {
  const { labelStartX = 0, labelWidth = 0, currentOrderY = 0, fontSize = 10 } = numberProperties;
  if (info.needsTwoLines) {
    // Draw recipient label (first line, bold)
    const normalizedRecipientLabel = normalizeTextForWinAnsi(info.recipientLabel);
    const recipientLabelWidth = boldFont.widthOfTextAtSize(normalizedRecipientLabel, fontSize);
    const recipientLabelX = labelStartX + labelWidth - recipientLabelWidth - 8;
    page.drawText(normalizedRecipientLabel, {
      x: recipientLabelX,
      y: dimensions.height - currentOrderY,
      font: boldFont,
      size: fontSize,
      color: rgb(0, 0, 0),
    });

    // Draw vendor label (second line, regular)
    const normalizedVendorLabel = normalizeTextForWinAnsi(info.vendorLabel);
    const vendorLabelWidth = font.widthOfTextAtSize(normalizedVendorLabel, fontSize);
    const vendorLabelX = labelStartX + labelWidth - vendorLabelWidth - 8;
    page.drawText(normalizedVendorLabel, {
      x: vendorLabelX,
      y: dimensions.height - currentOrderY - 8, // 8px below first line
      font: font,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  } else {
    // Single line - draw normally
    const normalizedLabelText = normalizeTextForWinAnsi(info.recipientLabel);
    const labelTextWidth = boldFont.widthOfTextAtSize(normalizedLabelText, fontSize);
    const labelX = labelStartX + labelWidth - labelTextWidth - 8;
    page.drawText(normalizedLabelText, {
      x: labelX,
      y: dimensions.height - currentOrderY,
      font: boldFont,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  }
};

export const drawOrderInfo = (options: DrawOrderInfoOptions) => {
  const { page, purchaseOrder, dimensions, startY, font, boldFont, vendorLocale, recipientLocale, isEdiPartner, sltCustomerAccountNumber } =
    options;
  const orderLineHeight = 10;

  // Determine font size based on paper size
  const fontSize = getScaledFontSizeForSmallPaper(9, 7, dimensions); // Smaller font for 3x4

  // Helper function to create translated labels
  const tVendor = getFixedT(vendorLocale);
  const tRecipient = recipientLocale && recipientLocale !== vendorLocale ? getFixedT(recipientLocale) : undefined;

  const createOrderLabel = (key: string, fallback: string) => {
    const vendorLabel = tVendor(key, { defaultValue: fallback });
    if (tRecipient) {
      const recipientLabel = tRecipient(key, { defaultValue: fallback });
      return {
        vendorLabel,
        recipientLabel,
        needsTwoLines: vendorLabel !== recipientLabel,
      };
    }
    return {
      vendorLabel,
      recipientLabel: vendorLabel,
      needsTwoLines: false,
    };
  };

  // Helper function to format dates with locale
  const formatDateWithLocale = (date: Date, locale: Locale) => {
    const localeString = locale.replace("_", "-"); // Convert en_US to en-US
    // Use shorter date format to avoid truncation
    return date.toLocaleDateString(localeString, {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Always display Order #, and ADD retailer/SLT Order # as an additional row above if retailerOrderNumber exists
  // This matches the development branch logic: always show Order #, then add retailer order number if available
  const orderInfo = [
    {
      ...createOrderLabel("components.packingSlip.orderInfo.orderNumber", "Order #"),
      value: purchaseOrder.purchaseOrderId,
    },
    {
      ...createOrderLabel("components.packingSlip.orderInfo.orderDate", "Order Date"),
      value: formatDateWithLocale(new Date(purchaseOrder.createdAt), recipientLocale ?? vendorLocale),
    },
    {
      ...createOrderLabel("components.packingSlip.orderInfo.shipDate", "Ship Date"),
      value: formatDateWithLocale(new Date(), recipientLocale ?? vendorLocale),
    },
  ];

  // Determine label based on customer account number (matches dev branch lines 408-410)
  // Use translated strings - the translation keys are determined below and passed to createOrderLabel
  const isSLTCustomerAccount = purchaseOrder.customerAccountNumber === sltCustomerAccountNumber;

  // Determine retailer order number: EDI partners use webOrderNumber, others use externalOrderNumber
  const retailerOrderNumber = isEdiPartner ? purchaseOrder.webOrderNumber : purchaseOrder.externalOrderNumber;

  // Add retailer/SLT Order # row at the top if retailerOrderNumber exists
  if (retailerOrderNumber) {
    const labelKey = isSLTCustomerAccount
      ? "components.packingSlip.orderInfo.sltOrderNumber"
      : "components.packingSlip.orderInfo.retailerOrderNumber";
    const labelFallback = isSLTCustomerAccount ? "SLT Order #" : "Retailer Order #";
    orderInfo.unshift({
      ...createOrderLabel(labelKey, labelFallback),
      value: retailerOrderNumber,
    });
  }

  // Calculate flexible widths - start from right edge and extend left as needed
  const rightEdge = dimensions.width - dimensions.margin;
  const labelWidth = 60; // Fixed width for labels
  const borderWidth = 2; // Width of the border line
  const padding = 2; // Padding between elements

  // Calculate the maximum width needed for values by checking all order info
  const maxValueWidth = Math.max(...orderInfo.map((info) => font.widthOfTextAtSize(info.value, fontSize)));
  // Add extra padding to ensure no truncation occurs
  const totalWidthNeeded = labelWidth + borderWidth + padding + maxValueWidth + padding + 10;

  // Position from right edge, but ensure we don't go beyond the left margin
  // Allow the section to extend further left for longer order numbers
  const minLeftMargin = dimensions.margin;
  const startX = Math.max(minLeftMargin, rightEdge - totalWidthNeeded) + 5; // Move 5 pixels to the right
  const labelStartX = startX;
  const borderX = labelStartX + labelWidth + padding;
  const valueStartX = borderX + borderWidth + padding;

  // Calculate the actual right edge for values (may extend beyond original rightEdge)
  // Add extra padding to ensure no truncation occurs
  const actualRightEdge = Math.max(rightEdge, valueStartX + maxValueWidth + padding + 10);
  const valueWidth = actualRightEdge - valueStartX - padding;

  // Calculate wrapped heights for all order info values
  const wrappedInfo = orderInfo.map((info) => {
    // Calculate how many lines are needed for wrapping
    const avgCharWidth = fontSize * 0.55;
    const maxCharsPerLine = Math.floor(valueWidth / avgCharWidth);
    const wrappedLines = Math.ceil(info.value.length / maxCharsPerLine);

    return {
      ...info,
      needsWrapping: true, // Always use wrapping for order details
      wrappedLines,
      actualValueWidth: valueWidth,
    };
  });

  // Calculate total height needed for all order info (accounting for two-line labels and wrapped values)
  let totalOrderInfoHeight = 0;
  for (const info of wrappedInfo) {
    const labelHeight = info.needsTwoLines ? orderLineHeight + 8 : orderLineHeight;
    // Since we're always using wrapping, calculate the actual wrapped height
    const valueHeight = info.wrappedLines * orderLineHeight;
    totalOrderInfoHeight += Math.max(labelHeight, valueHeight);
  }

  // Draw vertical line separator (between labels and values, starting from top)
  const borderTopY = dimensions.height - startY + 7; // Start from top of first line, moved up slightly
  const borderBottomY = dimensions.height - startY - totalOrderInfoHeight + orderLineHeight - 5; // End at bottom of last line, extended down
  page.drawLine({
    start: { x: borderX, y: borderTopY },
    end: { x: borderX, y: borderBottomY },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  // Draw order info labels and values
  let currentOrderY = startY;
  for (const info of wrappedInfo) {
    // Draw label (right-aligned in label area)
    drawOrderInfoLabel(page, info, dimensions, font, boldFont, { currentOrderY, labelStartX, labelWidth, fontSize });

    // Always use wrapping for order details to ensure no truncation of important data
    const heightUsed = drawWrappedText(page, info.value, {
      x: valueStartX,
      y: dimensions.height - currentOrderY,
      maxWidth: valueWidth,
      fontSize,
      font,
      lineHeight: orderLineHeight,
    });

    // Adjust Y position based on whether we have one or two lines for the label
    const labelHeight = info.needsTwoLines ? orderLineHeight + 8 : orderLineHeight;
    currentOrderY += Math.max(heightUsed, labelHeight);
  }
};

interface DrawColumnTextOptions {
  page: PDFPage;
  text: string;
  column: { position: number; width: number; align: TextAlignment };
  columnIndex: number;
  dimensions: Dimensions;
  startY: number;
  fontSize: number;
  font: PDFFont;
  baseLineHeight: number;
}

// Helper function to draw column text with appropriate wrapping
const drawColumnText = (options: DrawColumnTextOptions): number => {
  const { page, text, column, columnIndex, dimensions, startY, fontSize, font, baseLineHeight } = options;

  // Subtract one character width for padding between columns
  const avgCharWidth = fontSize * 0.55;
  const paddingWidth = avgCharWidth;
  const adjustedWidth = Math.max(0, column.width - paddingWidth);

  // For item # column (index 1), use wrapping instead of truncation
  if (columnIndex === 1) {
    // Use wrapping for item # column
    return drawWrappedText(page, text, {
      x: column.position,
      y: dimensions.height - startY,
      maxWidth: adjustedWidth,
      fontSize,
      font,
      lineHeight: baseLineHeight,
    });
  } else {
    // Use wrapping with proper alignment for all columns to ensure no truncation of important data
    return drawWrappedTextWithAlignment(page, text, {
      x: column.position,
      y: dimensions.height - startY,
      maxWidth: adjustedWidth,
      fontSize,
      font,
      lineHeight: baseLineHeight,
      align: column.align,
    });
  }
};

export const drawTableRow = (
  page: PDFPage,
  dimensions: Dimensions,
  startY: number,
  lineItem: PurchaseOrderLine,
  font: PDFFont,
  formatLocale: Locale,
  translateGrindType?: (value: string | undefined) => string | undefined
): number => {
  const baseLineHeight = getScaledSpacing(10, 8, dimensions); // Tighter line height, especially for 3x4
  const fontSize = getScaledFontSizeForSmallPaper(8, 6, dimensions); // Smaller font for 3x4

  interface TableRowColumn {
    rowValue: string | number | undefined;
    position: number;
    width: number;
    align: TextAlignment;
  }

  const { quantity, sourceProductSku, lineItemWeightInGrams, grindType } = lineItem;

  // Translate grind type if translation function is provided
  const translatedGrindType = translateGrindType ? translateGrindType(grindType) : grindType;

  // Format title like OrderLineItemsCell does (remove weight from title and add calculated weight)
  const formattedProductTitle = formatProductTitle(lineItem, formatLocale);

  // Calculate column widths and positions using shared helper
  const { qtyWidth, itemWidth, descWidth, weightWidth, grindWidth, qtyPosition, itemPosition, descPosition, grindPosition } =
    calculateColumnDimensions(dimensions);

  const columns: TableRowColumn[] = [
    { rowValue: quantity ?? "-", position: qtyPosition, width: qtyWidth, align: "center" },
    { rowValue: sourceProductSku ?? "-", position: itemPosition, width: itemWidth, align: "left" },
    {
      rowValue: formattedProductTitle,
      position: descPosition,
      width: descWidth,
      align: "left",
    },
    {
      rowValue: formatWeightWithLocale(lineItemWeightInGrams, formatLocale),
      position: grindPosition - weightWidth - 5, // 5px gap between weight and grind
      width: weightWidth,
      align: "center",
    },
    { rowValue: translatedGrindType ?? "-", position: grindPosition, width: grindWidth, align: "center" },
  ];

  // Calculate line heights for all columns using wrapping
  const lineHeights = columns.map((column) => {
    const rowValue = String(column.rowValue ?? "");
    // Calculate how many lines are needed for wrapping
    const avgCharWidth = fontSize * 0.55;
    const maxCharsPerLine = Math.floor(column.width / avgCharWidth);
    const lines = Math.ceil(rowValue.length / maxCharsPerLine);
    return lines * baseLineHeight;
  });

  for (const [columnIndex, column] of columns.entries()) {
    if (column.rowValue !== undefined && column.rowValue !== null) {
      const text = String(column.rowValue);
      const heightUsed = drawColumnText({ page, text, column, columnIndex, dimensions, startY, fontSize, font, baseLineHeight });
      lineHeights[columnIndex] = heightUsed;
    }
  }

  // Sized after drawing so the next row always starts below every line this row put on the page.
  return startY + Math.max(...lineHeights, baseLineHeight);
};

export const drawGiftMessage = (
  page: PDFPage,
  purchaseOrder: PurchaseOrder,
  dimensions: Dimensions,
  startY: number,
  font: PDFFont
): number => {
  const { giftMessage } = purchaseOrder;
  if (!giftMessage) {
    return startY;
  }

  const lineHeight = 10;
  const fontSize = getScaledFontSizeForSmallPaper(8, 6, dimensions); // Smaller font for 3x4
  const normalizedGiftMessage = normalizeTextForWinAnsi(giftMessage);
  page.drawText(normalizedGiftMessage, {
    x: dimensions.margin,
    y: dimensions.height - startY,
    font,
    size: fontSize,
    color: rgb(0, 0, 0),
  });

  const lines = Math.ceil(giftMessage.length / dimensions.detailsMaxLength);
  return startY + lines * lineHeight + 10;
};

export const drawCustomerAccountDetails = (
  page: PDFPage,
  purchaseOrder: PurchaseOrder,
  dimensions: Dimensions,
  startY: number,
  font: PDFFont
): number => {
  const lineHeight = 8; // Tightened line height
  const fontSize = getScaledFontSizeForSmallPaper(8, 6, dimensions); // Smaller font for 3x4
  const { customerAccountNotes } = purchaseOrder;

  if (customerAccountNotes && customerAccountNotes.length > 0) {
    let currentY = startY;
    for (const note of customerAccountNotes) {
      if (note?.trim()) {
        // Use wrapText approach similar to promotion body for better width utilization
        const availableWidth = dimensions.width - dimensions.margin * 2;
        const lines = wrapText(note, availableWidth, fontSize);
        for (const [index, line] of lines.entries()) {
          page.drawText(line, {
            x: dimensions.margin,
            y: dimensions.height - (currentY + index * lineHeight),
            font,
            size: fontSize,
            color: rgb(0, 0, 0),
          });
        }
        const heightUsed = lines.length * lineHeight;
        currentY += heightUsed + 2; // Reduced spacing between notes
      }
    }
    return currentY + lineHeight;
  }

  return startY;
};

export const drawFooter = (page: PDFPage, dimensions: Dimensions, font: PDFFont, recipientLocale: Locale, vendorName?: string) => {
  if (!vendorName) {
    return; // Hide footer if vendor name is not available
  }
  const tRecipient = getFixedT(recipientLocale);
  const roastedByText = tRecipient("components.packingSlip.roastedBy", { defaultValue: "Roasted by" });
  const fontSize = getScaledFontSizeForSmallPaper(8, 6, dimensions); // Smaller font for 3x4
  const footerText = `${roastedByText} ${vendorName}`;
  const normalizedFooterText = normalizeTextForWinAnsi(footerText);
  page.drawText(normalizedFooterText, {
    x: dimensions.margin,
    y: dimensions.margin + 10,
    font,
    size: fontSize,
    color: rgb(0.5, 0.5, 0.5),
  });
};
